import { memo, useId, useLayoutEffect, useRef } from "react";
import issaAnimatedSvg from "../../../assets/issa-animated.svg?raw";
import styles from "./issaInsights.module.scss";

/** idle: blinks only. listening: the user is typing to her. thinking: head
 *  tilts. responding: head settles while the answer streams. done: one nod when
 *  the answer lands. */
export type IssaAvatarState = "idle" | "listening" | "thinking" | "responding" | "done";

const SVG_IDS = new Set(Array.from(issaAnimatedSvg.matchAll(/\bid="([^"]+)"/g), m => m[1]));

/** The SVG with every id and #id reference, and its issa-stN fill classes,
 *  suffixed per instance. Inlined, the SVG's <style> is page-global, so
 *  without this the dock and the quick chat would share rules: the last copy's
 *  fill classes (some point at its own gradient ids) would win for both. */
function scopedSvg(suffix: string): string {
  return issaAnimatedSvg
    .replace(/\bid="([^"]+)"/g, (_, id: string) => `id="${id}-${suffix}"`)
    .replace(/#([\w-]+)/g, (whole, id: string) => (SVG_IDS.has(id) ? `#${id}-${suffix}` : whole))
    .replace(/\bissa-st(\d+)\b/g, (_, n: string) => `issa-st${n}-${suffix}`);
}

/**
 * Issa as an inline SVG, so the head, eye and torso groups animate by state
 * (the rules live inside the SVG, keyed on its data-state). An <img> would
 * only ever play the default state.
 *
 * The markup is injected once, imperatively, and React never owns it. With
 * dangerouslySetInnerHTML, React 19 compares the {__html} object by identity
 * and rewrites innerHTML on every parent render - each poll reset the SVG to
 * its idle markup, so the head never tilted and jumped at every status change.
 */
export const IssaAnimatedAvatar = memo(function IssaAnimatedAvatar({ state }: { state: IssaAvatarState }) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const suffix = useId().replace(/[^\w-]/g, "");

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // Our own static asset, bundled at build time - never user content.
    if (!host.firstElementChild) host.innerHTML = scopedSvg(suffix);
    const svg = host.querySelector("svg");
    // Changing only the attribute keeps the blink cycle running across states.
    if (svg && svg.dataset.state !== state) svg.dataset.state = state;
  }, [state, suffix]);

  return <span ref={hostRef} className={styles.avatarAnimated} />;
});
